import { useState, useEffect, useCallback, useRef } from "react";
import { addDays, differenceInCalendarDays, format } from "date-fns";
import { toast } from "sonner";
import { CalendarDays, ShieldCheck, Tag, Loader2, Minus, Plus, Check, ChevronsRight, ChevronsLeft, MessageSquare, Mail, Upload, FileCheck2, IdCard } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiErrorDetail, money } from "@/lib/api";
import { AmenityIcon } from "@/lib/icons";
import { getHoliday } from "@/lib/holidays";
import { getCustomEvent } from "@/lib/events";
import { getRefCode } from "@/lib/affiliate";
import { useLang } from "@/context/LanguageContext";
import pricklyPearCluster from "@/assets/prickly-pear-cluster.png";
import hornyToad from "@/assets/horny-toad.png";

const iso = (d) => format(d, "yyyy-MM-dd");

const stripeStyle = () => ({
  backgroundImage: `repeating-linear-gradient(45deg, rgba(55,65,81,0.4) 0, rgba(55,65,81,0.4) 1.5px, transparent 1.5px, transparent 7px)`,
});

export const BookingSection = ({ property }) => {
  const { t } = useLang();
  const pricing = property?.pricing || {};
  const [range, setRange] = useState();
  const [guests, setGuests] = useState(2);
  const [disabled, setDisabled] = useState([{ before: new Date() }, { after: addDays(new Date(), 365) }]);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [promo, setPromo] = useState("");
  const [appliedPromo, setAppliedPromo] = useState("");
  const [upsells, setUpsells] = useState([]);
  const [selected, setSelected] = useState([]);
  const dp = property?.damage_protection;
  const dpEnabled = !!dp?.enabled;
  const [damageOption, setDamageOption] = useState("deposit");
  const [waiver, setWaiver] = useState({ first_name: "", last_name: "", address1: "", unit: "", city: "", state: "", postal_code: "", phone: "", email: "" });
  const setW = (k, v) => setWaiver((w) => ({ ...w, [k]: v }));
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState({ guest_name: "", guest_email: "", guest_phone: "", message: "", sms_opt_in: false, sms_marketing_opt_in: false });
  const [agree, setAgree] = useState(false);
  const [signature, setSignature] = useState("");
  const [agreeOpen, setAgreeOpen] = useState(false);
  const [idFile, setIdFile] = useState(null);
  const [idPath, setIdPath] = useState("");
  const [idType, setIdType] = useState("");
  const [idUploading, setIdUploading] = useState(false);
  const idInputRef = useRef(null);

  const handleIdUpload = async (file) => {
    if (!file) return;
    setIdUploading(true);
    setIdFile(file);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/bookings/upload-id", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setIdPath(data.path);
      setIdType(data.content_type);
      toast.success(t("ID uploaded"));
    } catch (err) {
      setIdFile(null);
      setIdPath("");
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setIdUploading(false);
    }
  };
  const [submitting, setSubmitting] = useState(false);
  const [smsPrompt, setSmsPrompt] = useState(false);
  const [months, setMonths] = useState(typeof window !== "undefined" && window.innerWidth >= 768 ? 2 : 1);
  const [priceMap, setPriceMap] = useState({});
  const [events, setEvents] = useState([]);
  const [fromRate, setFromRate] = useState(null);
  const [bookedMap, setBookedMap] = useState({});

  const fetchPrices = useCallback(async (fromDate) => {
    try {
      const { data } = await api.get(`/availability/prices?start=${iso(fromDate)}&end=${iso(addDays(fromDate, 90))}`);
      setPriceMap((prev) => ({ ...prev, ...data }));
    } catch { /* prices are non-critical */ }
  }, []);

  useEffect(() => { fetchPrices(new Date()); }, [fetchPrices]);

  useEffect(() => {
    api.get(`/availability/prices?start=${iso(new Date())}&end=${iso(addDays(new Date(), 365))}`)
      .then(({ data }) => {
        setPriceMap((prev) => ({ ...prev, ...data }));
        const vals = Object.values(data);
        if (vals.length) setFromRate(Math.min(...vals));
      }).catch(() => {});
  }, []);

  useEffect(() => { api.get("/events").then((r) => setEvents(r.data || [])).catch(() => {}); }, []);

  const renderDay = ({ date, activeModifiers }) => {
    const price = priceMap[iso(date)];
    const outside = activeModifiers?.outside;
    const showPrice = price && !activeModifiers?.disabled && !outside;
    const holiday = outside ? null : (getCustomEvent(date, events) || getHoliday(date));
    const src = outside ? null : bookedMap[iso(date)];
    return (
      <div className="relative flex flex-col items-center justify-center leading-none w-full h-full">
        {src ? <span className="absolute inset-0 rounded-md pointer-events-none" style={stripeStyle(src)} aria-label={`Booked (${src})`} /> : null}
        {holiday ? <holiday.Icon className="relative z-10 h-3 w-3 text-primary mb-0.5" aria-label={holiday.label}><title>{holiday.label}</title></holiday.Icon> : null}
        <span className="relative z-10">{date.getDate()}</span>
        {showPrice ? <span className="relative z-10 text-[11px] font-medium opacity-80 mt-0.5 tabular-nums">${price}</span> : null}
      </div>
    );
  };

  useEffect(() => {
    const onResize = () => setMonths(window.innerWidth >= 768 ? 2 : 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    api.get("/availability").then((res) => {
      const raw = res.data.blocked || [];
      const blocks = raw.map((b) => ({
        from: new Date(b.start + "T00:00:00"),
        to: addDays(new Date(b.end + "T00:00:00"), -1),
      }));
      setDisabled([{ before: new Date() }, { after: addDays(new Date(), 365) }, ...blocks]);
      const map = {};
      raw.forEach((b) => {
        let d = new Date(b.start + "T00:00:00");
        const end = new Date(b.end + "T00:00:00");
        while (d < end) { map[iso(d)] = b.source || "manual"; d = addDays(d, 1); }
      });
      setBookedMap(map);
    });
    api.get("/upsells").then((res) => setUpsells(res.data || []));
  }, []);

  const toggleUpsell = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const fetchQuote = useCallback(async (r, promoCode) => {
    if (!r?.from || !r?.to) { setQuote(null); return; }
    setQuoting(true);
    try {
      const { data } = await api.post("/availability/quote", {
        check_in: iso(r.from), check_out: iso(r.to), guests, promo_code: promoCode || null, upsell_ids: selected, damage_option: dpEnabled ? damageOption : null,
      });
      setQuote(data);
      if (promoCode && data.promo_code) { setAppliedPromo(data.promo_code); toast.success("Promo applied!"); }
    } catch (err) {
      setQuote(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      if (promoCode) setAppliedPromo("");
    } finally {
      setQuoting(false);
    }
  }, [guests, selected, damageOption, dpEnabled]);

  useEffect(() => {
    if (range?.from && range?.to) fetchQuote(range, appliedPromo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, guests, selected, damageOption]);

  const nights = range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) : 0;

  const submitBooking = async (smsOptIn) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        check_in: iso(range.from), check_out: iso(range.to), guests,
        ...details, sms_opt_in: smsOptIn,
        promo_code: appliedPromo || null, upsell_ids: selected, damage_option: dpEnabled ? damageOption : null,
        waiver_info: dpEnabled && damageOption === "waiver" ? waiver : null,
        origin_url: window.location.origin,
        ref_code: getRefCode(),
        agreement_accepted: agree,
        agreement_signature: signature.trim(),
        id_document_path: idPath,
        id_document_content_type: idType,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  const reserve = () => {
    if (dpEnabled && damageOption === "waiver") {
      const req = ["first_name", "last_name", "address1", "city", "state", "postal_code", "phone", "email"];
      const missing = req.some((k) => !String(waiver[k] || "").trim());
      if (missing) { toast.error("Please complete all required damage-waiver enrollment fields."); return; }
    }
    if (!details.sms_opt_in) { setSmsPrompt(true); return; }
    submitBooking(true);
  };

  const canReserve = quote && quote.available && nights > 0;

  const bookRef = useRef(null);
  const [toadWalking, setToadWalking] = useState(false);
  useEffect(() => {
    const el = bookRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setToadWalking(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={bookRef} id="book" className="relative isolate overflow-hidden py-20 md:py-28 bg-accent/40">
      <img
        src={pricklyPearCluster}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -z-10 left-0 bottom-0 h-[50%] w-auto object-contain"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 text-center">
          <p className="text-primary font-medium text-sm uppercase tracking-widest mb-2">{t("Reserve your dates")}</p>
          <h2 className="font-script text-6xl md:text-8xl leading-tight text-foreground">
            {t("Book your stay")}
          </h2>
          <p className="text-muted-foreground mt-3">{t("From")} {money(fromRate ?? pricing.nightly_rate, pricing.currency)} / {t("night")} · {t("Min")} {pricing.min_nights} {t("nights")}</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-3 min-w-0 w-full relative bg-card rounded-2xl p-3 sm:p-5 md:p-7 shadow-sm overflow-hidden">
            <div className="hidden lg:block pointer-events-none absolute bottom-2 left-[-11rem] z-10">
              <div className={`relative toad-hidden ${toadWalking ? "toad-crawl toad-crawling toad-looking" : ""}`}>
                <div aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 bottom-2 w-40 h-5 rounded-[50%] bg-black/45 blur-md" />
                <div className="toad-look">
                  <img src={hornyToad} alt="" aria-hidden="true" className="toad-bob relative w-44 h-auto object-contain" />
                </div>
              </div>
            </div>
            <div className="w-full max-w-full overflow-x-auto flex justify-center">
            <Calendar
              mode="range"
              numberOfMonths={months}
              showOutsideDays
              selected={range}
              onSelect={setRange}
              onMonthChange={(m) => fetchPrices(m)}
              disabled={disabled}
              components={{ DayContent: renderDay }}
              className="p-0 mx-auto"
              classNames={{
                head_cell: "text-muted-foreground rounded-md w-9 sm:w-11 md:w-12 font-normal text-[0.8rem]",
                day: "inline-flex flex-col items-center justify-center h-12 w-9 sm:w-11 md:w-12 p-0 font-normal rounded-md hover:bg-accent hover:text-accent-foreground aria-selected:opacity-100",
                day_outside: "invisible",
              }}
              data-testid="booking-calendar"
            />
            </div>
          </div>

          <div className="lg:col-span-2 min-w-0 w-full bg-card rounded-2xl p-6 md:p-7 shadow-sm lg:sticky lg:top-24" data-testid="quote-summary">
            <div className="flex items-center gap-2 text-foreground font-semibold mb-5">
              <CalendarDays className="h-5 w-5 text-primary" /> {t("Your stay")}
            </div>

            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="rounded-lg border border-border p-3">
                <div className="text-xs text-muted-foreground">{t("Check-in")}</div>
                <div className="font-medium text-foreground text-sm" data-testid="summary-checkin">{range?.from ? format(range.from, "MMM d, yyyy") : t("Select")}</div>
              </div>
              <div className="rounded-lg border border-border p-3">
                <div className="text-xs text-muted-foreground">{t("Check-out")}</div>
                <div className="font-medium text-foreground text-sm" data-testid="summary-checkout">{range?.to ? format(range.to, "MMM d, yyyy") : t("Select")}</div>
              </div>
            </div>

            <div className="flex items-center justify-between mb-5">
              <Label className="text-sm">{t("Guests")}</Label>
              <div className="flex items-center gap-3">
                <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full" data-testid="guests-minus" onClick={() => setGuests(Math.max(1, guests - 1))}><Minus className="h-4 w-4" /></Button>
                <span className="w-6 text-center font-medium" data-testid="guests-count">{guests}</span>
                <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full" data-testid="guests-plus" onClick={() => setGuests(Math.min(property?.max_guests || 6, guests + 1))}><Plus className="h-4 w-4" /></Button>
              </div>
            </div>

            {upsells.length > 0 && (
              <div className="mb-5" data-testid="upsells-list">
                <Label className="text-sm mb-2 block">{t("Enhance your stay")}</Label>
                <div className="space-y-2">
                  {upsells.map((u) => {
                    const on = selected.includes(u.id);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        data-testid={`upsell-${u.id}`}
                        onClick={() => toggleUpsell(u.id)}
                        className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-all ${
                          on ? "border-primary bg-primary/5" : "border-border hover:border-secondary"
                        }`}
                      >
                        <span className={`h-4 w-4 rounded flex-shrink-0 flex items-center justify-center border ${on ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
                          {on && <Check className="h-3 w-3 text-white" />}
                        </span>
                        <AmenityIcon name={u.icon} className="h-4 w-4 text-secondary flex-shrink-0" />
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-medium text-foreground truncate">{u.name}</span>
                          {u.description && <span className="block text-xs text-muted-foreground truncate">{u.description}</span>}
                        </span>
                        <span className="text-sm font-semibold text-foreground flex-shrink-0">+{money(u.price, pricing.currency)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {dpEnabled && (
              <div className="mb-5" data-testid="damage-protection-block">
                <Label className="text-sm mb-2 block">{t("Damage protection")}</Label>
                {dp?.description && <p className="text-xs text-muted-foreground mb-2">{t(dp.description)}</p>}
                <div className="space-y-2">
                  <button
                    type="button"
                    data-testid="damage-option-waiver"
                    onClick={() => setDamageOption("waiver")}
                    className={`w-full flex items-center gap-3 rounded-lg border px-3 py-3 text-left transition-all ${
                      damageOption === "waiver" ? "border-primary bg-primary/5" : "border-border hover:border-secondary"
                    }`}
                  >
                    <span className={`h-4 w-4 rounded-full flex-shrink-0 flex items-center justify-center border ${damageOption === "waiver" ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
                      {damageOption === "waiver" && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                    </span>
                    <ShieldCheck className="h-5 w-5 text-secondary flex-shrink-0" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-foreground">{t(dp?.waiver_label || "Damage waiver")}</span>
                      <span className="block text-xs text-muted-foreground">{t("Non-refundable fee — no large hold on your card.")}</span>
                    </span>
                    <span className="text-sm font-semibold text-foreground flex-shrink-0">
                      {quote?.damage_waiver_fee ? `+${money(quote.damage_waiver_fee, quote.currency)}` : `+${money(dp?.waiver_amount || 0, quote?.currency)}`}
                    </span>
                  </button>
                  {damageOption === "waiver" && (
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-3" data-testid="waiver-enrollment-form">
                      <p className="text-xs text-muted-foreground">{t("Required to enroll your damage waiver. This information is used for your coverage.")}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-xs">{t("First name")}</Label><Input value={waiver.first_name} onChange={(e) => setW("first_name", e.target.value)} className="rounded-lg h-9" data-testid="waiver-first-name" /></div>
                        <div><Label className="text-xs">{t("Last name")}</Label><Input value={waiver.last_name} onChange={(e) => setW("last_name", e.target.value)} className="rounded-lg h-9" data-testid="waiver-last-name" /></div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="col-span-2"><Label className="text-xs">{t("Street address")}</Label><Input value={waiver.address1} onChange={(e) => setW("address1", e.target.value)} className="rounded-lg h-9" data-testid="waiver-address1" /></div>
                        <div><Label className="text-xs">{t("Unit # (optional)")}</Label><Input value={waiver.unit} onChange={(e) => setW("unit", e.target.value)} className="rounded-lg h-9" data-testid="waiver-unit" /></div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div><Label className="text-xs">{t("City")}</Label><Input value={waiver.city} onChange={(e) => setW("city", e.target.value)} className="rounded-lg h-9" data-testid="waiver-city" /></div>
                        <div><Label className="text-xs">{t("State")}</Label><Input value={waiver.state} onChange={(e) => setW("state", e.target.value)} className="rounded-lg h-9" data-testid="waiver-state" /></div>
                        <div><Label className="text-xs">{t("Postal code")}</Label><Input value={waiver.postal_code} onChange={(e) => setW("postal_code", e.target.value)} className="rounded-lg h-9" data-testid="waiver-postal" /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-xs">{t("Phone")}</Label><Input value={waiver.phone} onChange={(e) => setW("phone", e.target.value)} className="rounded-lg h-9" data-testid="waiver-phone" /></div>
                        <div><Label className="text-xs">{t("Email")}</Label><Input type="email" value={waiver.email} onChange={(e) => setW("email", e.target.value)} className="rounded-lg h-9" data-testid="waiver-email" /></div>
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    data-testid="damage-option-deposit"
                    onClick={() => setDamageOption("deposit")}
                    className={`w-full flex items-center gap-3 rounded-lg border px-3 py-3 text-left transition-all ${
                      damageOption === "deposit" ? "border-primary bg-primary/5" : "border-border hover:border-secondary"
                    }`}
                  >
                    <span className={`h-4 w-4 rounded-full flex-shrink-0 flex items-center justify-center border ${damageOption === "deposit" ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
                      {damageOption === "deposit" && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                    </span>
                    <ShieldCheck className="h-5 w-5 text-secondary flex-shrink-0" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-foreground">{t(dp?.deposit_label || "Refundable security deposit")}</span>
                      <span className="block text-xs text-muted-foreground">{t("Refunded after checkout if there's no damage. Charged with your balance.")}</span>
                    </span>
                    <span className="text-sm font-semibold text-foreground flex-shrink-0">{money(dp?.security_deposit || 0, quote?.currency)}</span>
                  </button>
                </div>
              </div>
            )}

            <div className="flex gap-2 mb-5">
              <div className="relative flex-1">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder={t("Promo code")} value={promo} onChange={(e) => setPromo(e.target.value)} className="pl-9 rounded-lg uppercase" data-testid="promo-input" />
              </div>
              <Button type="button" variant="secondary" className="rounded-lg" data-testid="promo-apply" disabled={!promo || !range?.to} onClick={() => fetchQuote(range, promo)}>{t("Apply")}</Button>
            </div>

            {quoting && <div className="flex items-center justify-center py-6 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> {t("Checking...")}</div>}

            {quote && !quoting && (
              <div className="space-y-2.5 text-sm border-t border-border pt-4">
                {!quote.available && (
                  <div className="bg-destructive/10 text-destructive rounded-lg px-3 py-2 text-sm mb-2" data-testid="unavailable-msg">
                    {t("These dates aren't available. Please pick different dates.")}
                  </div>
                )}
                <div className="flex justify-between text-muted-foreground">
                  <span>{quote.rate_varies ? `${quote.nights} ${t("nights")} (avg ${money(quote.avg_nightly, quote.currency)}/${t("night")})` : `${money(quote.nightly_rate, quote.currency)} × ${quote.nights} ${t("nights")}`}</span>
                  <span className="text-foreground">{money(quote.subtotal, quote.currency)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>{t("Cleaning fee")}</span><span className="text-foreground">{money(quote.cleaning_fee, quote.currency)}</span>
                </div>
                {quote.discount > 0 && (
                  <div className="flex justify-between text-secondary font-medium">
                    <span>{t("Discount")} ({quote.promo_code})</span><span>−{money(quote.discount, quote.currency)}</span>
                  </div>
                )}
                {(quote.upsells || []).map((u) => (
                  <div key={u.id} className="flex justify-between text-muted-foreground">
                    <span>{t(u.name)}</span><span className="text-foreground">{money(u.price, quote.currency)}</span>
                  </div>
                ))}
                {quote.damage_waiver_fee > 0 && (
                  <div className="flex justify-between text-muted-foreground" data-testid="summary-damage-waiver">
                    <span>{t(quote.damage_waiver_label || "Damage waiver")}</span><span className="text-foreground">{money(quote.damage_waiver_fee, quote.currency)}</span>
                  </div>
                )}
                {quote.security_deposit_amount > 0 && (
                  <div className="flex justify-between text-muted-foreground" data-testid="summary-security-deposit">
                    <span>{t(quote.damage_deposit_label || "Refundable security deposit")} <span className="text-[11px]">({t("refundable")})</span></span><span className="text-foreground">{money(quote.security_deposit_amount, quote.currency)}</span>
                  </div>
                )}
                {quote.tax_amount > 0 && (
                  <div className="flex justify-between text-muted-foreground" data-testid="summary-tax">
                    <span>{t("Lodging tax")} ({quote.tax_percent}%)</span><span className="text-foreground">{money(quote.tax_amount, quote.currency)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-foreground text-base border-t border-border pt-3">
                  <span>{t("Total")}</span><span data-testid="summary-total">{money(quote.total, quote.currency)}</span>
                </div>
                {quote.ota_savings > 0 && (
                  <div className="flex items-center justify-center gap-2 text-secondary bg-secondary/10 rounded-lg px-3 py-2 text-sm font-medium" data-testid="ota-savings-badge">
                    <ChevronsRight className="h-6 w-6 shrink-0 text-red-600 animate-arrow-flash" />
                    <span className="text-center">{t("You save approximately")} {money(quote.ota_savings, quote.currency)} {t("by booking direct vs. Airbnb/VRBO")}</span>
                    <ChevronsLeft className="h-6 w-6 shrink-0 text-red-600 animate-arrow-flash" />
                  </div>
                )}
                <div className="flex justify-between text-primary font-semibold bg-primary/5 rounded-lg px-3 py-2.5 mt-2">
                  <span>{t("Deposit due now")}</span><span data-testid="summary-deposit">{money(quote.deposit_amount, quote.currency)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground text-xs">
                  <span>{t("Balance due 30 days prior to check-in")}</span><span>{money(quote.balance_due, quote.currency)}</span>
                </div>
              </div>
            )}

            <Button
              className="w-full rounded-full bg-primary hover:bg-primary/90 h-12 mt-5 hover:-translate-y-0.5 transition-all"
              disabled={!canReserve}
              data-testid="reserve-btn"
              onClick={() => setOpen(true)}
            >
              {nights > 0 ? `${t("Reserve · pay")} ${money(quote?.deposit_amount || pricing.deposit_amount, pricing.currency)} ${t("deposit")}` : t("Select your dates")}
            </Button>
            <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground mt-3">
              <ShieldCheck className="h-3.5 w-3.5 text-secondary" /> {t("Secure checkout powered by Stripe")}
            </p>
          </div>
        </div>
      </div>

      {/* Sticky mobile booking bar */}
      {nights > 0 && (
        <div
          data-testid="mobile-booking-bar"
          className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-card/95 backdrop-blur-md border-t border-border px-4 py-3 flex items-center justify-between gap-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
        >
          <div className="min-w-0">
            {quote?.available ? (
              <>
                <div className="text-base font-semibold text-foreground leading-tight" data-testid="mobile-bar-deposit">{money(quote.deposit_amount, quote.currency)} <span className="text-xs font-normal text-muted-foreground">deposit</span></div>
                <div className="text-xs text-muted-foreground truncate">{nights} {nights === 1 ? "night" : "nights"} · {money(quote.total, quote.currency)} total</div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">Checking availability…</div>
            )}
          </div>
          <Button
            className="rounded-full bg-primary hover:bg-primary/90 h-11 px-6 shrink-0"
            disabled={!canReserve}
            data-testid="mobile-reserve-btn"
            onClick={() => setOpen(true)}
          >
            {t("Reserve")}
          </Button>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="booking-details-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{t("Almost there!")}</DialogTitle>
            <DialogDescription className="sr-only">{t("Enter your details, sign the rental agreement, and upload your ID to complete your booking.")}</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">
            {range?.from && range?.to && `${format(range.from, "MMM d")} – ${format(range.to, "MMM d, yyyy")} · ${guests} ${t("guests")}`}
          </p>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="b-name">{t("Full name")}</Label>
              <Input id="b-name" data-testid="booking-name" autoComplete="name" value={details.guest_name} onChange={(e) => setDetails({ ...details, guest_name: e.target.value })} className="mt-1.5 rounded-lg" placeholder="Jane Doe" />
            </div>
            <div>
              <Label htmlFor="b-email">{t("Email")}</Label>
              <Input id="b-email" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" data-testid="booking-email" value={details.guest_email} onChange={(e) => setDetails({ ...details, guest_email: e.target.value })} className="mt-1.5 rounded-lg" placeholder="jane@email.com" />
            </div>
            <div>
              <Label htmlFor="b-phone">{t("Mobile phone (for text updates)")}</Label>
              <Input id="b-phone" type="tel" inputMode="tel" autoComplete="tel" data-testid="booking-phone" value={details.guest_phone} onChange={(e) => setDetails({ ...details, guest_phone: e.target.value })} className="mt-1.5 rounded-lg" placeholder="+1 ..." />
              <label className="flex items-start gap-2.5 mt-3 cursor-pointer" data-testid="sms-consent-label">
                <Checkbox
                  checked={details.sms_opt_in}
                  onCheckedChange={(v) => setDetails({ ...details, sms_opt_in: !!v })}
                  className="mt-0.5"
                  data-testid="sms-opt-in-checkbox"
                />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  {t("I agree to receive reservation-related text messages (booking confirmation, check-in details, balance reminders) from MYAZBNB.COM at the mobile number above. Message frequency varies. Msg & data rates may apply. Reply HELP for help, STOP to cancel.")}{" "}
                  <a href="/sms" target="_blank" rel="noopener noreferrer" className="text-primary underline">{t("SMS Terms")}</a> · <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline">{t("Privacy")}</a>
                </span>
              </label>
              <label className="flex items-start gap-2.5 mt-3 cursor-pointer" data-testid="sms-marketing-consent-label">
                <Checkbox
                  checked={details.sms_marketing_opt_in}
                  onCheckedChange={(v) => setDetails({ ...details, sms_marketing_opt_in: !!v })}
                  className="mt-0.5"
                  data-testid="sms-marketing-opt-in-checkbox"
                />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  {t("(Optional) I also agree to receive promotional text messages about special offers and discounts from MYAZBNB.COM. Consent is not a condition of any purchase. Msg & data rates may apply. Reply STOP to cancel.")}
                </span>
              </label>
            </div>
            <div>
              <Label htmlFor="b-msg">{t("What brings you to Apache Junction?")}</Label>
              <Textarea id="b-msg" data-testid="booking-message" value={details.message} onChange={(e) => setDetails({ ...details, message: e.target.value })} className="mt-1.5 rounded-lg" placeholder={t("Arrival time, special requests...")} />
            </div>
            <div className="rounded-lg border border-border bg-accent/30 p-3 space-y-3">
              <label className="flex items-start gap-2.5 cursor-pointer" data-testid="agreement-consent-label">
                <Checkbox
                  checked={agree}
                  onCheckedChange={(v) => setAgree(!!v)}
                  className="mt-0.5"
                  data-testid="agreement-checkbox"
                />
                <span className="text-xs text-foreground leading-relaxed">
                  {t("I have read and agree to the")}{" "}
                  <button type="button" className="text-primary underline font-medium" data-testid="view-agreement-btn" onClick={() => setAgreeOpen(true)}>
                    {t("Rental Agreement")}
                  </button>
                  .
                </span>
              </label>
              <div>
                <Label htmlFor="b-sign" className="text-xs">{t("Type your full name to sign")}</Label>
                <Input id="b-sign" data-testid="agreement-signature" value={signature} onChange={(e) => setSignature(e.target.value)} className="mt-1.5 rounded-lg" placeholder={t("Full legal name")} autoComplete="name" />
              </div>
            </div>
            <div className="rounded-lg border border-border bg-accent/30 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <IdCard className="h-4 w-4 text-primary" />
                <Label className="text-xs font-medium text-foreground">{t("Government-issued photo ID (required)")}</Label>
              </div>
              <p className="text-[11px] text-muted-foreground">{t("For verification and insurance, please upload a photo of your driver's license or passport. Kept private and shared only with your host.")}</p>
              <input
                ref={idInputRef}
                type="file"
                accept="image/*,.pdf,.heic,.heif"
                className="hidden"
                data-testid="id-upload-input"
                onChange={(e) => handleIdUpload(e.target.files?.[0])}
              />
              {idPath ? (
                <div className="flex items-center gap-2 rounded-lg bg-secondary/10 border border-secondary/30 px-3 py-2" data-testid="id-uploaded-badge">
                  <FileCheck2 className="h-4 w-4 text-secondary flex-shrink-0" />
                  <span className="text-xs text-foreground truncate flex-1">{idFile?.name || t("ID uploaded")}</span>
                  <button type="button" className="text-[11px] text-primary underline" data-testid="id-replace-btn" onClick={() => idInputRef.current?.click()}>{t("Replace")}</button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full rounded-lg h-10"
                  disabled={idUploading}
                  data-testid="id-upload-btn"
                  onClick={() => idInputRef.current?.click()}
                >
                  {idUploading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> {t("Uploading...")}</> : <><Upload className="h-4 w-4 mr-2" /> {t("Upload ID")}</>}
                </Button>
              )}
            </div>
          </div>
          <Button
            className="w-full rounded-full bg-primary hover:bg-primary/90 h-12"
            disabled={submitting || !details.guest_name || !details.guest_email || !agree || !signature.trim() || !idPath}
            data-testid="pay-deposit-btn"
            onClick={reserve}
          >
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> {t("Redirecting...")}</> : `${t("Pay")} ${money(quote?.deposit_amount, quote?.currency)} ${t("deposit")}`}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={agreeOpen} onOpenChange={setAgreeOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="agreement-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{t("Rental Agreement")}</DialogTitle>
            <DialogDescription>{t("Please read the full agreement below before you sign.")}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto whitespace-pre-line text-sm text-foreground leading-relaxed pr-2" data-testid="agreement-text">
            {property?.rental_agreement || t("The rental agreement will be provided at booking.")}
          </div>
          <Button
            className="w-full rounded-full bg-primary hover:bg-primary/90 h-11 mt-2"
            data-testid="agreement-accept-btn"
            onClick={() => { setAgree(true); setAgreeOpen(false); }}
          >
            {t("I have read and agree")}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={smsPrompt} onOpenChange={setSmsPrompt}>
        <DialogContent className="sm:max-w-md" data-testid="sms-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{t("One quick thing about how we'll reach you")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1 leading-relaxed">
            {t("You haven't opted in to text messages. Time-sensitive updates like check-in instructions, gate codes, and arrival details are fastest by text. Without it, all communication will happen through email only — please make sure you'll be checking your inbox (including spam).")}
          </p>
          <label className="flex items-start gap-2.5 mt-1 rounded-lg border border-border bg-accent/30 p-3 cursor-pointer" data-testid="sms-prompt-consent-label">
            <Checkbox
              checked={details.sms_opt_in}
              onCheckedChange={(v) => setDetails({ ...details, sms_opt_in: !!v })}
              className="mt-0.5"
              data-testid="sms-prompt-opt-in-checkbox"
            />
            <span className="text-xs text-muted-foreground leading-relaxed">
              {t("Yes, text me reservation updates — I agree to receive reservation-related text messages (booking confirmation, check-in details, balance reminders) from MYAZBNB.COM at the mobile number I provided. Message frequency varies. Msg & data rates may apply. Reply HELP for help, STOP to cancel.")}
            </span>
          </label>
          <div className="flex flex-col gap-2 mt-2">
            <Button
              className="w-full rounded-full bg-primary hover:bg-primary/90 h-11"
              disabled={!details.sms_opt_in || submitting}
              data-testid="sms-prompt-optin-continue"
              onClick={() => { setSmsPrompt(false); submitBooking(true); }}
            >
              <MessageSquare className="h-4 w-4 mr-2" /> {t("Opt in & continue to payment")}
            </Button>
            <Button
              variant="outline"
              className="w-full rounded-full h-11"
              disabled={submitting}
              data-testid="sms-prompt-email-only"
              onClick={() => { setSmsPrompt(false); submitBooking(false); }}
            >
              <Mail className="h-4 w-4 mr-2" /> {t("No thanks — email me instead")}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground text-center">
            {t("By choosing email me instead you understand you will not receive SMS updates and are responsible for monitoring your email for all reservation communication.")}
          </p>
        </DialogContent>
      </Dialog>
    </section>
  );
};
